/**
 * The three shell primitives the design system declares (ADR-026): modal, drawer,
 * toast. Nothing here imports a component library and nothing here chooses a colour
 * that is not a token.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The toast tones are the status trio the product already has: teal success, gold
 * attention, and the single refusal red. Refusal red is the only red in the product.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { strings } from "../brand/strings";

/* ---------------------------------------------------------------------- */
/* Modal: a blocking conversation over the page.                           */
/* ---------------------------------------------------------------------- */

export function QModal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}): React.JSX.Element | null {
  if (!open) {
    return null;
  }
  return (
    <div className="q-overlay" data-testid="q-modal">
      <div className="q-overlay__scrim" onClick={onClose} aria-hidden="true" />
      <div className="q-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="q-modal__head">
          <h3 className="q-modal__title">{title}</h3>
          <button type="button" className="q-button q-button--quiet" onClick={onClose}>
            {strings.common.close}
          </button>
        </div>
        <div className="q-modal__body">{children}</div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Drawer: a panel that slides over the working area, from the end edge.   */
/* ---------------------------------------------------------------------- */

export function QDrawer({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}): React.JSX.Element | null {
  if (!open) {
    return null;
  }
  return (
    <div className="q-overlay" data-testid="q-drawer">
      <div className="q-overlay__scrim" onClick={onClose} aria-hidden="true" />
      <aside className="q-drawer" role="complementary" aria-label={title}>
        <div className="q-modal__head">
          <h3 className="q-modal__title">{title}</h3>
          <button type="button" className="q-button q-button--quiet" onClick={onClose}>
            {strings.common.close}
          </button>
        </div>
        <div className="q-modal__body">{children}</div>
      </aside>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Toast: the shell's transient notices, one tone per meaning.             */
/* ---------------------------------------------------------------------- */

export type ToastTone = "success" | "attention" | "danger";

export interface Toast {
  readonly id: number;
  readonly tone: ToastTone;
  readonly body: string;
}

interface ToastApi {
  readonly toasts: readonly Toast[];
  readonly announce: (tone: ToastTone, body: string) => void;
  readonly dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** The toast API; throws outside a provider, like the session context does. */
export function useToasts(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) {
    throw new Error("A screen asked for toasts outside a toast host");
  }
  return api;
}

export function ToastProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const announce = useCallback((tone: ToastTone, body: string) => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, tone, body }]);
  }, []);

  const api = useMemo<ToastApi>(() => ({ toasts, announce, dismiss }), [toasts, announce, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="q-toasts" role="status" data-testid="q-toasts">
        {toasts.map((toast) => (
          <div key={toast.id} className={`q-toast q-toast--${toast.tone}`}>
            <p className="q-toast__body">{toast.body}</p>
            <button
              type="button"
              className="q-button q-button--quiet q-toast__dismiss"
              onClick={() => {
                dismiss(toast.id);
              }}
            >
              {strings.common.close}
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
